import { Tooltip as ChakraTooltip, type TooltipProps } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";

const CLOSE_DELAY_MS = 150;

// @chakra-ui/tooltip 2.6 gives the positioner pointer-events: none and closes on leaving the trigger (WCAG SC 1.4.13).
export const Tooltip = ({ isOpen: isOpenProp, onOpen, onClose, closeDelay, children, ...props }: TooltipProps) => {
  const [isOpen, setOpen] = useState(false);
  const overTooltip = useRef(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const controlled = isOpenProp !== undefined;

  const open = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const closeUnlessHovered = useCallback(() => {
    if (!overTooltip.current) close();
  }, [close]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      overTooltip.current = false;
      close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  if (controlled) {
    return (
      <ChakraTooltip isOpen={isOpenProp} onOpen={onOpen} onClose={onClose} closeDelay={closeDelay} {...props}>
        {children}
      </ChakraTooltip>
    );
  }

  return (
    <ChakraTooltip
      {...props}
      isOpen={isOpen}
      onOpen={open}
      onClose={closeUnlessHovered}
      closeDelay={closeDelay ?? CLOSE_DELAY_MS}
      pointerEvents="auto"
      onPointerEnter={(event) => {
        overTooltip.current = true;
        window.clearTimeout(closeTimer.current);
        props.onPointerEnter?.(event);
      }}
      onPointerLeave={(event) => {
        overTooltip.current = false;
        closeTimer.current = window.setTimeout(close, closeDelay ?? CLOSE_DELAY_MS);
        props.onPointerLeave?.(event);
      }}
      onClick={(event) => {
        overTooltip.current = false;
        close();
        clickThrough(event.currentTarget, event.clientX, event.clientY);
        props.onClick?.(event);
      }}
    >
      {children}
    </ChakraTooltip>
  );
};

const clickThrough = (tooltip: HTMLElement, x: number, y: number) => {
  const previous = tooltip.style.pointerEvents;
  tooltip.style.pointerEvents = "none";
  const beneath = document.elementFromPoint?.(x, y);
  tooltip.style.pointerEvents = previous;
  if (beneath instanceof HTMLElement && !tooltip.contains(beneath)) beneath.click();
};
